import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { SearchIcon, ShoppingBagIcon, MenuIcon, PhoneIcon, ChevronDownIcon, XIcon, MinusIcon, PlusIcon } from 'lucide-react';
export function Header() {
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const {
    cart,
    totalItems,
    subtotal,
    removeFromCart,
    updateQuantity
  } = useCart();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const timeoutRef = useRef<any>(null);
  const handleMouseEnter = (menu: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(menu);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 100);
  };
  const popularSearches = ['KENTE', 'BESPOKE', 'ACCESSORIES', 'WOMEN', 'MEN'];
  const megaMenuData = [{
    title: 'NEW & FEATURED',
    items: ['NEW IN', 'HIS & HERS', 'SALE'],
    images: [{
      url: 'https://cdn.magicpatterns.com/uploads/pJKfmPcBjErq76jQrTYk2j/image.png',
      label: 'New Arrivals'
    }, {
      url: 'https://cdn.magicpatterns.com/uploads/bxTL7vVABPe8YuDE1LiVeu/image.png',
      label: 'Best Sellers'
    }, {
      url: 'https://cdn.magicpatterns.com/uploads/tAhHTkZdpHhiBHvZ4nHim3/image.png',
      label: 'Lookbook'
    }]
  }, {
    title: 'COLLECTIONS',
    items: ['WOMEN', 'CURVE COLLECTION', 'PROM DRESSES', 'WEDDING DRESSES', 'SUMMER TIME COLLECTION', 'MEN', 'UNISEX'],
    images: [{
      url: 'https://images.unsplash.com/photo-1617137984095-74e4e5e3613f?auto=format&fit=crop&q=80&w=600',
      label: 'Menswear'
    }, {
      url: 'https://images.unsplash.com/photo-1566174053879-31528523f8ae?auto=format&fit=crop&q=80&w=600',
      label: 'Womenswear'
    }, {
      url: 'https://images.unsplash.com/photo-1598554747436-c9293d6a588f?auto=format&fit=crop&q=80&w=600',
      label: 'Bespoke'
    }]
  }, {
    title: 'FAMILY',
    items: ['DADDY & ME', 'MOMMY & ME', 'KIDS'],
    images: [{
      url: 'https://images.unsplash.com/photo-1622290291468-a28f7a7dc6a8?auto=format&fit=crop&q=80&w=600',
      label: 'Kids Collection'
    }, {
      url: 'https://cdn.magicpatterns.com/uploads/9ZXEJ3jfY9TKoS3Uy7tuvX/image.png',
      label: 'Mommy & Me'
    }, {
      url: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&q=80&w=600',
      label: 'Family Sets'
    }]
  }, {
    title: 'LIFESTYLE',
    items: ['ACCESSORIES', 'FABRIC'],
    images: [{
      url: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&q=80&w=600',
      label: 'Jewelry'
    }, {
      url: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&q=80&w=600',
      label: 'Footwear'
    }, {
      url: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&q=80&w=600',
      label: 'Skin Care'
    }]
  }];
  return <header className="sticky top-0 z-50 w-full bg-white border-b border-grey-divider" onMouseLeave={handleMouseLeave} data-id="element-152">
      <div className="max-w-[1800px] mx-auto px-6 h-20 flex items-center justify-between relative" onMouseEnter={() => setActiveMenu(null)} data-id="element-153">
        {/* Left Column: Mobile Menu & Desktop Nav */}
        <div className="flex-1 flex items-center min-w-0" data-id="element-154">
          <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden hover:opacity-50 transition-opacity flex-shrink-0" data-id="element-155">
            <MenuIcon className="w-5 h-5 stroke-[1.5px]" data-id="element-156" />
          </button>

          <a href="tel:5718423307" className="hidden md:flex items-center space-x-2 hover:opacity-50 transition-opacity flex-shrink-0" data-id="element-157">
            <PhoneIcon className="w-4 h-4 stroke-[1.5px]" data-id="element-158" />
            <span className="text-[11px] font-display font-bold tracking-widest uppercase" data-id="element-159">
              (571) 842-3307
            </span>
          </a>
        </div>

        {/* Center Column: Logo */}
        <div className="flex-shrink-0 px-4" data-id="element-160">
          <Link to="/" className="block text-center" data-id="element-161">
            <h1 className="text-sm md:text-2xl font-display font-bold tracking-[0.25em] md:tracking-[0.3em] uppercase leading-[1.1] md:leading-none md:whitespace-nowrap" data-id="element-162">
              <span className="block md:inline" data-id="element-163">Djily</span>
              <span className="block md:inline md:ml-2" data-id="element-164">Creations</span>
            </h1>
          </Link>
        </div>

        {/* Right Column: Actions */}
        <div className="flex-1 flex items-center justify-end space-x-6 min-w-0" data-id="element-165">
          <button onClick={() => setIsSearchOpen(true)} className="hover:opacity-50 transition-opacity flex-shrink-0" data-id="element-166">
            <SearchIcon className="w-5 h-5 stroke-[1.5px]" data-id="element-167" />
          </button>
          <button onClick={() => setIsCartOpen(true)} className="hover:opacity-50 transition-opacity relative flex-shrink-0" data-id="element-168">
            <ShoppingBagIcon className="w-5 h-5 stroke-[1.5px]" data-id="element-169" />
            {totalItems > 0 && <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-black text-white w-3.5 h-3.5 rounded-full flex items-center justify-center" data-id="element-170">
                {totalItems}
              </span>}
          </button>
        </div>
      </div>

      {/* Mega Menu Nav */}
      <nav className="hidden lg:block border-t border-grey-divider relative" data-id="element-171">
        <div className="max-w-[1800px] mx-auto px-6 flex items-center justify-center space-x-12" data-id="element-172">
          <Link to="/lookbook" onMouseEnter={() => setActiveMenu(null)} className={`py-4 text-[12px] font-display font-bold uppercase tracking-widest transition-all relative ${pathname === '/lookbook' ? 'text-black' : 'text-black/40 hover:text-black'}`} data-id="element-173">
            Lookbook
            {pathname === '/lookbook' && <motion.div layoutId="activeNav" className="absolute bottom-0 left-0 right-0 h-[2px] bg-black" data-id="element-174" />}
          </Link>
          <Link to="/alterations" onMouseEnter={() => setActiveMenu(null)} className={`py-4 text-[12px] font-display font-bold uppercase tracking-widest transition-all relative ${pathname === '/alterations' ? 'text-black' : 'text-black/40 hover:text-black'}`} data-id="element-175">
            Alterations
            {pathname === '/alterations' && <motion.div layoutId="activeNav" className="absolute bottom-0 left-0 right-0 h-[2px] bg-black" data-id="element-176" />}
          </Link>
          {megaMenuData.map(group => {
          const isGroupActive = group.items.some(item => pathname === `/${item.toLowerCase().replace(/\s+/g, '-')}`);
          return <div key={group.title} className="relative" onMouseEnter={() => handleMouseEnter(group.title)} data-id="element-177">
                <button className={`py-4 px-2 flex items-center space-x-1 text-[12px] font-display font-bold uppercase tracking-widest transition-all ${isGroupActive || activeMenu === group.title ? 'text-black' : 'text-black/40 hover:text-black'}`} data-id="element-178">
                  <span data-id="element-179">{group.title}</span>
                  <ChevronDownIcon className={`w-3 h-3 transition-transform ${activeMenu === group.title ? 'rotate-180' : ''}`} data-id="element-180" />
                </button>

                {/* Mega Menu Dropdown */}
                <div className={`fixed left-0 right-0 bg-white border-b border-grey-divider transition-all duration-300 z-40 ${activeMenu === group.title ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`} style={{
              top: '121px'
            }} onMouseEnter={() => handleMouseEnter(group.title)} data-id="element-181">
                  <div className="max-w-[1800px] mx-auto px-6 py-6 grid grid-cols-4 gap-12" data-id="element-182">
                    <div className="col-span-1 border-r border-grey-divider pr-8" data-id="element-183">
                      <h4 className="text-[12px] font-display font-bold text-black mb-4 tracking-[0.2em] uppercase" data-id="element-184">
                        Shop {group.title}
                      </h4>
                      <ul className="space-y-3" data-id="element-185">
                        {group.items.map(item => {
                      const itemPath = `/${item.toLowerCase().replace(/\s+/g, '-')}`;
                      const isActive = pathname === itemPath;
                      return <li key={item} data-id="element-186">
                              <Link to={itemPath} onClick={() => setActiveMenu(null)} className={`text-[13px] font-display font-bold uppercase tracking-widest transition-all duration-300 block ${isActive ? 'text-black pl-2' : 'text-black/40 hover:text-black hover:pl-2'}`} data-id="element-187">
                                {item}
                              </Link>
                            </li>;
                    })}
                      </ul>
                    </div>
                    <div className="col-span-3 grid grid-cols-3 gap-4" data-id="element-188">
                      {group.images.map((img, i) => <div key={i} className="aspect-video bg-grey-divider overflow-hidden relative group/img" data-id="element-189">
                          <img src={img.url} alt={img.label} className="w-full h-full object-cover grayscale contrast-125 group-hover/img:scale-105 group-hover/img:grayscale-0 transition-all duration-700" data-id="element-190" />
                          <div className="absolute inset-0 bg-black/10 group-hover/img:bg-black/0 transition-colors" data-id="element-191" />
                          <div className="absolute bottom-3 left-3 text-[8px] font-display tracking-widest uppercase text-white drop-shadow-md" data-id="element-192">
                            {img.label}
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </nav>

      {/* Search Modal Overlay */}
      <AnimatePresence data-id="element-193">
        {isSearchOpen && <div className="fixed inset-0 z-[110] flex items-start justify-center pt-4 sm:pt-20 px-4 sm:px-6" data-id="element-194">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsSearchOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-195" />

            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: -20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: -20
        }} className="relative w-full max-w-3xl bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" data-id="element-196">
              {/* Search Input Area */}
              <div className="p-4 sm:p-8 flex items-center space-x-3 sm:space-x-4 border-b border-grey-divider" data-id="element-197">
                <SearchIcon className="w-5 h-5 sm:w-6 sm:h-6 text-black/30" data-id="element-198" />
                <input autoFocus type="text" placeholder="Search products..." className="flex-1 text-base sm:text-xl font-light outline-none placeholder:text-black/20" onKeyDown={e => e.key === 'Escape' && setIsSearchOpen(false)} data-id="element-199" />
                <button onClick={() => setIsSearchOpen(false)} className="p-2 hover:bg-black/5 rounded-full transition-colors" data-id="element-200">
                  <XIcon className="w-5 h-5 sm:w-6 sm:h-6 text-black/30" data-id="element-201" />
                </button>
              </div>

              {/* Popular Searches Area */}
              <div className="p-4 sm:p-8 bg-white overflow-y-auto" data-id="element-202">
                <h4 className="text-[10px] font-display text-black/40 tracking-widest uppercase mb-4 sm:mb-6" data-id="element-203">
                  Popular Searches
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-204">
                  {popularSearches.map(tag => <button key={tag} className="px-4 sm:px-6 py-2 sm:py-2.5 bg-grey-divider/30 hover:bg-grey-divider/60 rounded-full text-[10px] sm:text-[11px] font-display font-bold tracking-widest transition-colors" data-id="element-205">
                      {tag}
                    </button>)}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-4 sm:px-8 py-4 sm:py-6 bg-grey-divider/10 flex items-center justify-between mt-auto" data-id="element-206">
                <div className="hidden sm:flex items-center space-x-2 text-[10px] font-display tracking-widest text-black/40 uppercase" data-id="element-207">
                  <span data-id="element-208">Press</span>
                  <span className="px-2 py-1 bg-white border border-grey-divider rounded-md text-black font-bold" data-id="element-209">
                    ESC
                  </span>
                  <span data-id="element-210">to close</span>
                </div>
                <button className="w-full sm:w-auto bg-black text-white px-10 py-3 rounded-xl text-[11px] font-display font-bold tracking-widest hover:bg-black/80 transition-colors uppercase" data-id="element-211">
                  Search
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Shopping Cart Drawer */}
      <AnimatePresence data-id="element-212">
        {isCartOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsCartOpen(false)} className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" data-id="element-213" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[130] shadow-2xl flex flex-col" data-id="element-214">
              <div className="p-6 border-b border-grey-divider flex items-center justify-between" data-id="element-215">
                <h2 className="text-sm font-display font-bold tracking-widest uppercase" data-id="element-216">
                  Shopping Bag ({totalItems})
                </h2>
                <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-black/5 rounded-full transition-colors" data-id="element-217">
                  <XIcon className="w-5 h-5" data-id="element-218" />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto" data-id="element-219">
                {cart.length === 0 ? <div className="h-full flex flex-col items-center justify-center p-12 text-center" data-id="element-220">
                    <ShoppingBagIcon className="w-12 h-12 text-black/10 mb-6 stroke-[1px]" data-id="element-221" />
                    <p className="text-[10px] font-display tracking-[0.3em] uppercase text-black/40 mb-8" data-id="element-222">
                      Your bag is currently empty
                    </p>
                    <Link to="/new-in" onClick={() => setIsCartOpen(false)} className="bg-black text-white px-10 py-4 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-black/80 transition-colors" data-id="element-223">
                      Start Shopping
                    </Link>
                  </div> : <div className="p-6 space-y-8" data-id="element-224">
                    {cart.map(item => <div key={`${item.id}-${item.size}-${item.color}`} className="flex space-x-6" data-id="element-225">
                        <div className="w-24 aspect-[3/4] bg-grey-divider overflow-hidden flex-shrink-0" data-id="element-226">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-227" />
                        </div>
                        <div className="flex-grow py-1 flex flex-col justify-between" data-id="element-228">
                          <div data-id="element-229">
                            <h3 className="text-xs font-display font-bold uppercase tracking-widest mb-1" data-id="element-230">
                              {item.name}
                            </h3>
                            <div className="space-y-0.5 text-[10px] font-display tracking-widest uppercase text-black/50" data-id="element-231">
                              {item.size && <p data-id="element-232">Size: {item.size}</p>}
                              {item.color && <p data-id="element-233">Color: {item.color}</p>}
                              <p data-id="element-234">Qty: {item.quantity}</p>
                            </div>
                          </div>
                          <div className="flex justify-between items-end" data-id="element-235">
                            <p className="text-xs font-display font-bold" data-id="element-236">
                              {item.price}
                            </p>
                            <button onClick={() => removeFromCart(item.id, item.size, item.color)} className="text-[10px] font-display uppercase tracking-widest border-b border-black/20 hover:border-black transition-colors" data-id="element-237">
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>

              <div className="p-8 border-t border-grey-divider space-y-6" data-id="element-238">
                <div className="flex justify-between items-baseline" data-id="element-239">
                  <span className="text-xs font-display font-bold tracking-widest uppercase text-black/40" data-id="element-240">
                    Subtotal
                  </span>
                  <span className="text-lg font-display font-bold" data-id="element-241">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <button onClick={() => {
              setIsCartOpen(false);
              navigate('/checkout');
            }} className="w-full bg-black text-white py-5 text-xs font-display font-bold uppercase tracking-[0.3em] hover:bg-black/80 transition-colors" data-id="element-242">
                  Checkout
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-243">
        {isMobileMenuOpen && <motion.div initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed inset-0 z-[100] bg-white flex flex-col md:hidden" data-id="element-244">
            <div className="h-20 px-6 flex items-center justify-between border-b border-grey-divider" data-id="element-245">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-display font-bold tracking-[0.2em] uppercase" data-id="element-246">
                Djily Creations
              </Link>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 hover:bg-black/5 rounded-full transition-colors" data-id="element-247">
                <XIcon className="w-5 h-5 stroke-[1.5px]" data-id="element-248" />
              </button>
            </div>

            <div className="flex-grow py-8 px-8 overflow-y-auto" data-id="element-249">
              <div className="space-y-10" data-id="element-250">
                <nav className="space-y-6" data-id="element-251">
                  {[{
                name: 'New In',
                path: '/new-in'
              }, {
                name: 'Lookbook',
                path: '/lookbook'
              }, {
                name: 'Alterations',
                path: '/alterations'
              }].map((link, i) => <motion.div key={link.name} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.1
              }} data-id="element-252">
                      <Link to={link.path} onClick={() => setIsMobileMenuOpen(false)} className="block text-[13px] font-display font-bold tracking-[0.2em] uppercase text-black hover:text-black/60 transition-colors" data-id="element-253">
                        {link.name}
                      </Link>
                    </motion.div>)}
                </nav>

                <div className="space-y-8 pt-8 border-t border-grey-divider" data-id="element-254">
                  <p className="text-[10px] font-display font-bold tracking-[0.3em] uppercase text-black/30" data-id="element-255">
                    Collections
                  </p>
                  <div className="grid grid-cols-1 gap-8" data-id="element-256">
                    {megaMenuData.map((group, i) => <motion.div key={group.title} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.3 + i * 0.1
                }} className="space-y-4" data-id="element-257">
                        <p className="text-[11px] font-display font-bold tracking-[0.15em] uppercase text-black/60 border-l-2 border-black/10 pl-3" data-id="element-258">
                          {group.title}
                        </p>
                        <div className="flex flex-col space-y-3 pl-3" data-id="element-259">
                          {group.items.map(item => <Link key={item} to={`/${item.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setIsMobileMenuOpen(false)} className="text-[12px] font-display tracking-[0.1em] uppercase text-black/80 hover:text-black transition-colors" data-id="element-260">
                              {item}
                            </Link>)}
                        </div>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-grey-divider bg-grey-divider/5 space-y-6" data-id="element-261">
              <div className="flex flex-col space-y-4" data-id="element-262">
                <a href="tel:5718423307" className="flex items-center space-x-3 text-[11px] font-display font-bold tracking-widest uppercase text-black/60 hover:text-black transition-colors" data-id="element-263">
                  <PhoneIcon className="w-4 h-4 stroke-[1.5px]" data-id="element-264" />
                  <span data-id="element-265">(571) 842-3307</span>
                </a>
                <p className="text-[10px] font-display tracking-widest uppercase text-black/40" data-id="element-266">
                  Alexandria, VA Studio
                </p>
              </div>
              <button onClick={() => {
            setIsMobileMenuOpen(false);
            setIsCartOpen(true);
          }} className="w-full bg-black text-white py-5 text-[11px] font-display font-bold uppercase tracking-[0.2em] hover:bg-black/80 transition-colors flex items-center justify-center space-x-3" data-id="element-267">
                <ShoppingBagIcon className="w-4 h-4" data-id="element-268" />
                <span data-id="element-269">View Bag ({totalItems})</span>
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}